import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { servicesData } from '../data/servicesData';
import styles from './ServiceDetailPage.module.css';
import VisualShowcase from '../components/VisualShowcase/VisualShowcase';
import VisualCarousel from '../components/AnimatedVisuals/VisualCarousel';
import SEO from '../components/SEO/SEO';
import StructuredData from '../components/StructuredData/StructuredData';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = servicesData.find(s => s.id === serviceId);
  const contentRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.classList.add(styles.visible);
    }
  }, [serviceId]);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!service) {
    return (
      <div className={styles.detailPage}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <h2 className={styles.notFound}>Service not found!</h2>
          <p className={styles.errorMessage}>The service you're looking for doesn't exist.</p>
          <Link to="/services" className={styles.backLink}>
            <span>←</span> Back to Services
          </Link>
        </div>
      </div>
    );
  }

  const pageTitle = service 
    ? `${service.title} | ${service.category} Services - SolveDot Labs`
    : 'Service Not Found | SolveDot Labs';
  const pageDescription = service
    ? `${service.description} ${service.tagline}. Professional ${service.category} services by SolveDot Labs with 99% uptime guarantee.`
    : 'The service you are looking for does not exist.';

  return (
    <div className={styles.detailPage}>
      {service && (
        <>
          <SEO 
            title={pageTitle}
            description={pageDescription}
            keywords={`${service.title}, ${service.category}, ${service.features.join(', ')}, SolveDot Labs, Software Development`}
          />
          <StructuredData type="service" service={service} />
        </>
      )}
      {/* Scroll Progress Indicator */}
      <div className={styles.scrollProgressBar}>
        <div 
          className={styles.scrollProgressFill}
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
      {/* Hero Section */}
      <section className={styles.serviceHero}>
        <div className={styles.heroBackground}>
          <div className={styles.heroOrb1}></div>
          <div className={styles.heroOrb2}></div>
        </div>
        <div className={styles.heroContent}>
          <Link to="/services" className={styles.breadcrumb}>
            <span>←</span> All Services
          </Link>
          <div className={styles.serviceHeader}>
            <div className={styles.iconWrapper}>
              <div className={styles.iconGlow}></div>
              <span className={styles.icon}>{service.icon}</span>
            </div>
            <div className={styles.headerText}>
              <span className={styles.categoryBadge}>{service.category}</span>
              <h1 className={styles.title}>{service.title}</h1>
              <p className={styles.tagline}>{service.tagline}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.contentSection}>
        <div className={styles.detailContainer} ref={contentRef}>
          <div className={styles.mainContent}>
            <div className={styles.descriptionCard}>
              <h2 className={styles.sectionTitle}>Overview</h2>
              <p className={styles.description}>{service.description}</p>
            </div>
            
            <div className={styles.featuresCard}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.titleIcon}>✨</span>
                Key Features
              </h2>
              <ul className={styles.featuresList}>
                {service.features.map((feature, index) => (
                  <li key={index} className={styles.featureItem}>
                    <span className={styles.checkIcon}>✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.ctaCard}>
              <h3>Ready to Get Started?</h3>
              <p>Let's discuss how {service.title} can transform your business.</p>
              <div className={styles.ctaButtons}>
                <Link to="/contact" className={styles.primaryCta}>
                  Get a Free Consultation
                </Link>
                <Link to="/services" className={styles.secondaryCta}>
                  Explore Other Services
                </Link>
              </div>
            </div>
          </div>

          {/* Visual Showcase Section */}
          <div className={styles.visualSection}>
            <div className={styles.visualCard}>
              <h3 className={styles.visualTitle}>See It In Action</h3>
              {service.category === 'Manufacturing' || service.id === 'industry-4-0' ? (
                <VisualShowcase type="dashboard" />
              ) : service.visualType ? (
                <VisualShowcase type={service.visualType} />
              ) : (
                <VisualCarousel />
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
